import { useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, EmptyState } from "@/components/admin/ui";
import { BusinessHoursEditor } from "@/components/org/settings/BusinessHoursEditor";
import { normalizeStructuredHours } from "@/lib/businessHours";
import { Users2 } from "lucide-react";

const SOURCE_LABELS = { organization: "Organization hours", team: "Team hours", individual: "Individual hours" };

const WorkingHoursRow = ({ id, name, subtitle, endpoint, API }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [useParent, setUseParent] = useState(true);
  const [customHours, setCustomHours] = useState(normalizeStructuredHours(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggle = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    setError("");
    try {
      const { data: resp } = await axios.get(`${API}${endpoint}`, { withCredentials: true });
      setData(resp);
      setUseParent(resp.override?.use_parent_hours ?? true);
      setCustomHours(normalizeStructuredHours(resp.override?.structured || resp.effective?.hours));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const { data: resp } = await axios.patch(`${API}${endpoint}`, {
        use_parent_hours: useParent,
        structured: useParent ? null : customHours,
      }, { withCredentials: true });
      setData((d) => ({ ...d, override: resp.override, effective: resp.effective }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-white/10" data-testid={`office-working-hours-row-${id}`}>
      <button type="button" onClick={toggle} data-testid={`office-working-hours-toggle-${id}`} className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
        <div>
          <p className="text-sm font-bold text-slate-900 dark:text-white">{name}</p>
          {subtitle && <p className="text-xs text-slate-500 dark:text-slate-400">{subtitle}</p>}
        </div>
        {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {open && (
        <div className="border-t border-slate-200 p-4 dark:border-white/10">
          {error && <p className="mb-3 text-sm font-semibold text-red-600 dark:text-red-400" data-testid={`office-working-hours-error-${id}`}>{error}</p>}
          {!data ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : (
            <>
              <p className="mb-3 text-xs font-semibold text-slate-500 dark:text-slate-400" data-testid={`office-working-hours-effective-${id}`}>
                Currently effective: <span className="font-bold text-slate-700 dark:text-slate-200">{SOURCE_LABELS[data.effective.source]}</span>
              </p>
              <div className="mb-4 flex gap-2">
                <button type="button" onClick={() => setUseParent(true)} data-testid={`office-working-hours-use-parent-${id}`}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${useParent ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-300"}`}>
                  Inherit
                </button>
                <button type="button" onClick={() => setUseParent(false)} data-testid={`office-working-hours-use-custom-${id}`}
                  className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${!useParent ? "border-[#0052FF] bg-[#0052FF] text-white" : "border-slate-200 text-slate-600 dark:border-white/15 dark:text-slate-300"}`}>
                  Custom Hours
                </button>
              </div>
              {!useParent && <BusinessHoursEditor hours={customHours} onChange={setCustomHours} />}
              <div className="mt-4">
                <button type="button" onClick={save} disabled={saving} data-testid={`office-working-hours-save-${id}`}
                  className="rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50">
                  {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const OfficeWorkingHoursTab = ({ meta, API }) => (
  <div className="space-y-6" data-testid="office-working-hours-tab">
    <Panel title="Team Working Hours" subtitle="Each Team inherits Organization hours unless it has its own override." testId="office-team-hours-panel">
      {meta.teams.length === 0 ? (
        <EmptyState icon={Users2} title="No Teams yet." message="Create a Team in Team & Access to configure its working hours." testId="office-team-hours-empty" />
      ) : (
        <div className="space-y-2">
          {meta.teams.map((t) => (
            <WorkingHoursRow key={t.team_id} id={t.team_id} name={t.name} endpoint={`/org/office/teams/${t.team_id}/working-hours`} API={API} />
          ))}
        </div>
      )}
    </Panel>

    <Panel title="Individual Working Hours" subtitle="Each person inherits their Team's (or Organization's) hours unless given an individual override." testId="office-individual-hours-panel">
      {meta.users.length === 0 ? (
        <EmptyState icon={Users2} title="No users in view." message="No individuals are visible for you to configure." testId="office-individual-hours-empty" />
      ) : (
        <div className="space-y-2">
          {meta.users.map((u) => (
            <WorkingHoursRow key={u.user_id} id={u.user_id} name={u.name} subtitle={!u.active ? "Deactivated" : undefined} endpoint={`/org/office/users/${u.user_id}/working-hours`} API={API} />
          ))}
        </div>
      )}
    </Panel>
  </div>
);
